import { hashTextSync } from "./hash.js";
import { stripMarkdownNoise } from "./normalize.js";
import type { ApplyMode, EditOp, OfficeTextFormat } from "./types.js";

const MAX_EDITS = 24;

function parseFormat(raw: unknown): OfficeTextFormat | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const f = raw as Record<string, unknown>;
  const out: OfficeTextFormat = {};
  if (typeof f.bold === "boolean") out.bold = f.bold;
  if (typeof f.italic === "boolean") out.italic = f.italic;
  if (typeof f.underline === "boolean") out.underline = f.underline;
  if (typeof f.strike === "boolean") out.strike = f.strike;
  if (typeof f.color === "string" && f.color.trim()) out.color = f.color.trim();
  if (typeof f.highlight === "string" && f.highlight.trim()) {
    out.highlight = f.highlight.trim();
  }
  const align = String(f.alignment ?? "");
  if (
    align === "Left" ||
    align === "Centered" ||
    align === "Right" ||
    align === "Justified"
  ) {
    out.alignment = align;
  }
  if (typeof f.fontSize === "number" && f.fontSize > 0) out.fontSize = f.fontSize;
  if (typeof f.style === "string" && f.style.trim()) out.style = f.style.trim();
  if (typeof f.rtl === "boolean") out.rtl = f.rtl;
  return Object.keys(out).length ? out : undefined;
}

function parseApplyMode(raw: unknown): ApplyMode | undefined {
  const m = String(raw ?? "").toLowerCase();
  if (m === "all" || m === "first" || m === "selection") return m;
  return undefined;
}

function metaFrom(e: Record<string, unknown>): {
  apply_mode?: ApplyMode;
  content_hash?: string;
  op_id?: string;
  clause_id?: string;
} {
  const apply_mode = parseApplyMode(e.apply_mode);
  const content_hash =
    typeof e.content_hash === "string" && e.content_hash.trim()
      ? e.content_hash.trim()
      : undefined;
  const op_id =
    typeof e.op_id === "string" && e.op_id.trim()
      ? e.op_id.trim()
      : undefined;
  const clause_id =
    typeof e.clause_id === "string" && e.clause_id.trim()
      ? e.clause_id.trim()
      : typeof e.clause_key === "string" && e.clause_key.trim()
        ? e.clause_key.trim()
        : undefined;
  return {
    ...(apply_mode ? { apply_mode } : {}),
    ...(content_hash ? { content_hash } : {}),
    ...(op_id ? { op_id } : {}),
    ...(clause_id ? { clause_id } : {}),
  };
}

/**
 * Normalize/validate raw model JSON into EditOp[].
 * Default apply_mode for find-based ops is left unset (host decides:
 * all when unscoped, selection when scoped).
 */
export function normalizeEditOps(raw: unknown, max = MAX_EDITS): EditOp[] {
  if (!Array.isArray(raw)) return [];
  const out: EditOp[] = [];
  for (const item of raw.slice(0, max)) {
    if (!item || typeof item !== "object") continue;
    const e = item as Record<string, unknown>;
    const type = String(e.type || "");
    const format = parseFormat(e.format);
    const meta = metaFrom(e);

    if (type === "replace") {
      const find = stripMarkdownNoise(String(e.find ?? "")).trim();
      if (!find) continue;
      out.push({
        type: "replace",
        find,
        replace: stripMarkdownNoise(String(e.replace ?? "")),
        ...(format ? { format } : {}),
        ...meta,
        content_hash: meta.content_hash ?? hashTextSync(find),
      });
    } else if (type === "replace_selection") {
      out.push({
        type: "replace_selection",
        replace: stripMarkdownNoise(String(e.replace ?? "")),
        ...(format ? { format } : {}),
        ...meta,
      });
    } else if (type === "replace_all") {
      const replace = stripMarkdownNoise(String(e.replace ?? ""));
      if (!replace.trim()) continue;
      out.push({ type: "replace_all", replace, ...meta });
    } else if (type === "insert") {
      const insert = stripMarkdownNoise(String(e.insert ?? ""));
      if (!insert) continue;
      const atRaw = String(e.at ?? "selection");
      const at =
        atRaw === "end" || atRaw === "start" || atRaw === "selection"
          ? atRaw
          : "selection";
      out.push({
        type: "insert",
        insert,
        at,
        ...(format ? { format } : {}),
        ...meta,
      });
    } else if (type === "insert_after") {
      const content = stripMarkdownNoise(
        String(e.content ?? e.insert ?? "")
      ).trim();
      if (!content) continue;
      const anchor_find = stripMarkdownNoise(
        String(e.anchor_find ?? e.find ?? "")
      ).trim();
      out.push({
        type: "insert_after",
        content,
        ...(anchor_find ? { anchor_find } : {}),
        ...(format ? { format } : {}),
        ...meta,
        content_hash:
          meta.content_hash ??
          (anchor_find ? hashTextSync(anchor_find) : undefined),
      });
    } else if (type === "delete") {
      const find = stripMarkdownNoise(String(e.find ?? "")).trim();
      if (!find) continue;
      out.push({
        type: "delete",
        find,
        ...meta,
        content_hash: meta.content_hash ?? hashTextSync(find),
      });
    } else if (type === "format") {
      if (!format) continue;
      const find = stripMarkdownNoise(String(e.find ?? "")).trim();
      const targetRaw = String(e.target ?? (find ? "find" : "selection"));
      const target = targetRaw === "find" ? "find" : "selection";
      if (target === "find" && !find) continue;
      out.push({
        type: "format",
        target,
        ...(find ? { find } : {}),
        format,
        ...meta,
        content_hash:
          meta.content_hash ?? (find ? hashTextSync(find) : undefined),
      });
    } else if (type === "comment") {
      const comment = stripMarkdownNoise(String(e.comment ?? "")).trim();
      if (!comment) continue;
      const find = stripMarkdownNoise(String(e.find ?? "")).trim();
      out.push({
        type: "comment",
        comment,
        ...(find ? { find } : {}),
        ...meta,
        content_hash:
          meta.content_hash ?? (find ? hashTextSync(find) : undefined),
      });
    }
  }
  return out;
}

/** Resolve effective apply mode for a find-based op. */
export function resolveApplyMode(
  op: EditOp,
  opts?: { hasSelectionScope?: boolean }
): ApplyMode {
  if (op.apply_mode) return op.apply_mode;
  if (opts?.hasSelectionScope) return "selection";
  if (op.type === "replace_selection") return "selection";
  if (
    op.type === "format" &&
    (op.target === "selection" || !("find" in op && op.find))
  ) {
    return "selection";
  }
  return "all";
}

export function isFullDocRewriteRequest(message: string): boolean {
  const t = message.trim();
  return /کل\s*(سند|قرارداد|متن)|از\s*نو\s*بنویس|بازنویس(ی)?\s*کل|replace\s*all|rewrite\s*(the\s*)?(whole|entire)\s*(doc|document|contract)/i.test(
    t
  );
}
